import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Link,
  Outlet,
  ScrollRestoration,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { motion as Motion } from "framer-motion";
import { createPortal } from "react-dom";

import cls from "./LoungeLayout.module.scss";
import i18n from "@/i18n";
import api from "@/utils/axios";
import { useAuth } from "@/app/auth-context-state";
import { useNotifications } from "@/app/notification-context-state";

import FooterMobile from "@/components/NotyFooter/FooterMobile";
import FooterDesktop from "@/components/NotyFooter/FooterDesktop";
import CallOverlay from "@/components/Calls/CallOverlay";

import avatarPng from "@/assets/UI/avatar.png";
import homeIcon from "@/assets/UI/home.webp";
import menuIcon from "@/assets/UI/menu.webp";
import friendsIcon from "@/assets/UI/friends.webp";
import clubIcon from "@/assets/UI/club.webp";
import chatIcon from "@/assets/UI/chat.webp";
import searchIcon from "@/assets/UI/search.webp";
import notificationsIcon from "@/assets/UI/notifications.webp";
import walletIcon from "@/assets/UI/wallet.webp";
import callIcon from "@/assets/UI/audioCall.webp";
import whoIcon from "@/assets/UI/who.webp";
import lawIcon from "@/assets/UI/law.webp";
import lanIcon from "@/assets/UI/lan.webp";
import fbIcon from "@/assets/UI/fb.webp";
import instagramIcon from "@/assets/UI/instagram.webp";
import youtubeIcon from "@/assets/UI/youtube.webp";
import tiktokIcon from "@/assets/UI/tiktok.webp";
import logoutIcon from "@/assets/UI/logout.webp";

import manIcon from "@/assets/UI/man.webp";
import womanIcon from "@/assets/UI/woman.webp";
import non_binaryIcon from "@/assets/UI/non_binary.webp";
import trans_manIcon from "@/assets/UI/trans_man.webp";
import trans_womanIcon from "@/assets/UI/trans_woman.webp";
import genderfluidIcon from "@/assets/UI/genderfluid.webp";
import self_describeIcon from "@/assets/UI/self_describe.webp";

const fadeIn = {
  hidden: { opacity: 0, y: -8 },
  show: { opacity: 1, y: 0, transition: { duration: 0.25, ease: "easeOut" } },
};

const GENDER_ICON_MAP = {
  man: manIcon,
  woman: womanIcon,
  non_binary: non_binaryIcon,
  trans_man: trans_manIcon,
  trans_woman: trans_womanIcon,
  genderfluid: genderfluidIcon,
  self_describe: self_describeIcon,
  not_select: self_describeIcon,
};

function getGenderIconSrc(gender) {
  return GENDER_ICON_MAP[gender] || self_describeIcon;
}

const loungeLayoutFallbacks = {
  hi: "Hi",
  profileAriaLabel: "Open profile",
  couple: "Couple",
  gender: "Gender",
  profileRefreshError: "Can't refresh profile",
  notifications: "Notifications",
  wallet: "Wallet",
  chat: "Chat",
  calls: "Calls",
  menu: "Menu",
  openMenu: "Open menu",
  closeMenu: "Close menu",
  mainMenu: "Main Menu",
  quickActions: "Quick actions",
  home: "Home",
  friendsFeed: "Friends feed",
  clubsFeed: "Clubs feed",
  search: "Search",
  yourLounge: "Your Lounge",
  homeFeed: "Home feed",
  profileAndMoney: "Profile & Money",
  myProfile: "My profile",
  aboutMe: "About me",
  editProfile: "Edit profile",
  eTransfer: "E-transfer",
  redeemCode: "Redeem code",
  findAndExplore: "Find & Explore",
  searchMembers: "Search members",
  myFriends: "My friends",
  friendRequests: "Friend requests",
  searchClubs: "Search clubs",
  exploreUniverse: "Explore universe",
  myClubs: "My clubs",
  utilities: "Utilities",
  language: "Pick your language",
  aboutUs: "Wanna know who we are?",
  terms: "The Noty Law Book",
  facebook: "Join our Facebook",
  instagram: "Check our Instagram",
  youtube: "Watch our YouTube",
  tiktok: "Follow us on TikTok",
  logOut: "Log out",
};

const tLoungeLayout = (key) =>
  i18n.t(`loungeLayout:${key}`, {
    defaultValue: i18n.t(`homeLayout.${key}`, {
      defaultValue: loungeLayoutFallbacks[key] ?? key,
    }),
  });

const MEMBER_CITIZENSHIP_TYPES = Object.freeze({
  EXPLORER: "explorer_citizen",
  NEXUS: "nexus_citizen",
  LUMORA: "lumora_citizen",
});

function getCitizenshipTheme(citizenshipType) {
  const type = String(citizenshipType || "").toLowerCase();
  if (type === MEMBER_CITIZENSHIP_TYPES.NEXUS) return "nexus";
  if (type === MEMBER_CITIZENSHIP_TYPES.LUMORA) return "lumora";
  return "explorer";
}

export default function LoungeLayout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const closeBtnRef = useRef(null);
  const mainRef = useRef(null);

  const { pathname, hash } = useLocation();
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const { unreadCount, unreadChatCount } = useNotifications() || {};

  const [mlLoading, setMlLoading] = useState(true);
  const [mlData, setMlData] = useState(null);
  const [mlError, setMlError] = useState(null);

  // Load layout info
  useEffect(() => {
    let ignore = false;

    (async () => {
      try {
        setMlLoading(true);
        setMlError(null);
        const { data } = await api.get("/v1/lounge-layout");
        if (!ignore) setMlData(data || null);
      } catch (err) {
        if (!ignore) setMlError(err);
      } finally {
        if (!ignore) setMlLoading(false);
      }
    })();

    return () => {
      ignore = true;
    };
  }, []);

  const mlInitialLoading = mlLoading && !mlData && !mlError;
  const member = mlData?.hasMember ? mlData.member : null;

  const avatarSrc = member?.avatar?.url || avatarPng;
  const rawUsername = member?.username || user?.username || "N";
  const memberType = member?.memberType || user?.member_type || "single";
  const isCouple = memberType === "couple";

  const rawGender = member?.gender ?? user?.member_gender ?? "not_select";
  const genderSrc = getGenderIconSrc(rawGender);

  const rawPartnerGender =
    member?.partnerGender ?? user?.partner_gender ?? null;
  const partnerGenderSrc = rawPartnerGender
    ? getGenderIconSrc(rawPartnerGender)
    : null;
  const citizenshipType = member?.citizenshipType ?? user?.citizenshipType;
  const citizenshipTheme = getCitizenshipTheme(citizenshipType);

  const unreadNotificationsCount =
    typeof unreadCount === "number" && !Number.isNaN(unreadCount)
      ? unreadCount
      : (mlData?.unreadNotificationsCount ?? 0);

  const unreadChatBadgeCount =
    typeof unreadChatCount === "number" && !Number.isNaN(unreadChatCount)
      ? unreadChatCount
      : (mlData?.unreadChatCount ?? 0);

  // Scroll reset on route change (unless using hash)
  useEffect(() => {
    if (hash) return;
    mainRef.current?.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, hash]);

  // Menu UX: lock body scroll + ESC to close + focus close
  useEffect(() => {
    if (!menuOpen) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };

    window.addEventListener("keydown", onKeyDown);
    requestAnimationFrame(() => closeBtnRef.current?.focus());

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  const handleLogout = useCallback(async () => {
    try {
      await api.post("/v1/auth/logout", {}, { withCredentials: true });
    } catch {
      // ignore
    } finally {
      localStorage.removeItem("cm_at");
      sessionStorage.removeItem("cm_at");
      delete api.defaults?.headers?.common?.Authorization;
      setUser(null);
      window.location.replace("/login");
    }
  }, [setUser]);

  const QuickIcon = ({
    src,
    alt,
    ring = "pink",
    active = false,
    className = "",
  }) => {
    const ringClass =
      ring === "pink"
        ? cls.ringPink
        : ring === "blue"
          ? cls.ringBlue
          : ring === "purple"
            ? cls.ringPurple
            : ring === "mint"
              ? cls.ringMint
              : ring === "gold"
                ? cls.ringGold
                : ring === "black"
                  ? cls.ringBlack
                  : ring === "red"
                    ? cls.ringNotifRed
                    : cls.ringBlack;

    return (
      <span
        className={`${cls.iconImgWrap} ${ringClass} ${active ? cls.iconActive : ""} ${className}`.trim()}
        aria-hidden="true"
      >
        <img className={cls.iconImg} src={src} alt={alt || ""} />
      </span>
    );
  };

  const hiText = tLoungeLayout("hi");

  // ---------- One menu, everything included ----------
  const menuSections = useMemo(() => {
    const env = import.meta.env;

    return [
      {
        title: tLoungeLayout("yourLounge"),
        items: [
          {
            type: "internal",
            to: "/lounge",
            label: tLoungeLayout("homeFeed"),
            icon: homeIcon,
          },
          {
            type: "internal",
            to: "/lounge/friends-feed",
            label: tLoungeLayout("friendsFeed"),
            icon: friendsIcon,
          },
          {
            type: "internal",
            to: "/lounge/clubs-feed",
            label: tLoungeLayout("clubsFeed"),
            icon: clubIcon,
          },
          {
            type: "internal",
            to: "/lounge/my-notifications",
            label: tLoungeLayout("notifications"),
            icon: notificationsIcon,
          },
          {
            type: "internal",
            to: "/lounge/chat",
            label: tLoungeLayout("chat"),
            icon: chatIcon,
          },
          {
            type: "internal",
            to: "/lounge/calls",
            label: tLoungeLayout("calls"),
            icon: callIcon,
          },
        ],
      },
      {
        title: tLoungeLayout("profileAndMoney"),
        items: [
          {
            type: "internal",
            to: "/lounge/my-profile",
            label: tLoungeLayout("myProfile"),
            icon: avatarPng,
          },
          {
            type: "internal",
            to: "/lounge/my-profile/about",
            label: tLoungeLayout("aboutMe"),
            icon: whoIcon,
          },
          {
            type: "internal",
            to: "/lounge/my-profile/edit",
            label: tLoungeLayout("editProfile"),
            icon: menuIcon,
          },
          {
            type: "internal",
            to: "/lounge/wallet",
            label: tLoungeLayout("wallet"),
            icon: walletIcon,
          },
          {
            type: "internal",
            to: "/lounge/wallet/e-transfer",
            label: tLoungeLayout("eTransfer"),
            icon: walletIcon,
          },
          {
            type: "internal",
            to: "/lounge/wallet/redeem",
            label: tLoungeLayout("redeemCode"),
            icon: walletIcon,
          },
        ],
      },
      {
        title: tLoungeLayout("findAndExplore"),
        items: [
          {
            type: "internal",
            to: "/lounge/search",
            label: tLoungeLayout("searchMembers"),
            icon: searchIcon,
          },
          {
            type: "internal",
            to: "/lounge/friends",
            label: tLoungeLayout("myFriends"),
            icon: friendsIcon,
          },
          {
            type: "internal",
            to: "/lounge/friends/requests",
            label: tLoungeLayout("friendRequests"),
            icon: friendsIcon,
          },
          {
            type: "internal",
            to: "/lounge/search-clubs",
            label: tLoungeLayout("searchClubs"),
            icon: clubIcon,
          },
          {
            type: "internal",
            to: "/lounge/explore-universe",
            label: tLoungeLayout("exploreUniverse"),
            icon: homeIcon,
          },
          {
            type: "internal",
            to: "/lounge/my-clubs",
            label: tLoungeLayout("myClubs"),
            icon: clubIcon,
          },
        ],
      },
      {
        title: tLoungeLayout("utilities"),
        items: [
          {
            type: "external-internal",
            to: "/language",
            label: tLoungeLayout("language"),
            icon: lanIcon,
          },
          {
            type: "external-internal",
            to: "/about-us",
            label: tLoungeLayout("aboutUs"),
            icon: whoIcon,
          },
          {
            type: "external-internal",
            to: "/terms",
            label: tLoungeLayout("terms"),
            icon: lawIcon,
          },

          { type: "divider" },

          {
            type: "external",
            href: env.VITE_SOCIAL_FACEBOOK,
            label: tLoungeLayout("facebook"),
            icon: fbIcon,
          },
          {
            type: "external",
            href: env.VITE_SOCIAL_INSTAGRAM,
            label: tLoungeLayout("instagram"),
            icon: instagramIcon,
          },
          {
            type: "external",
            href: env.VITE_SOCIAL_YOUTUBE,
            label: tLoungeLayout("youtube"),
            icon: youtubeIcon,
          },
          {
            type: "external",
            href: env.VITE_SOCIAL_TIKTOK,
            label: tLoungeLayout("tiktok"),
            icon: tiktokIcon,
          },

          { type: "divider" },

          {
            type: "action",
            onClick: handleLogout,
            label: tLoungeLayout("logOut"),
            icon: logoutIcon,
            danger: true,
          },
        ],
      },
    ];
  }, [handleLogout]);

  const MenuItem = ({ item }) => {
    if (item.type === "divider") {
      return <li className={cls.menuDivider} aria-hidden="true" />;
    }

    const commonClass = `${cls.menuCard} ${item.danger ? cls.menuCardDanger : ""}`;

    // internal link
    if (item.type === "internal") {
      return (
        <li>
          <Link
            to={item.to}
            className={commonClass}
            onClick={() => setMenuOpen(false)}
          >
            <img
              className={cls.menuCardIcon}
              src={item.icon}
              alt=""
              loading="lazy"
            />
            <span className={cls.menuCardLabel}>{item.label}</span>
            <span className={cls.menuCardChevron} aria-hidden="true">
              ›
            </span>
          </Link>
        </li>
      );
    }

    // internal but open new tab (language/about/terms)
    if (item.type === "external-internal") {
      return (
        <li>
          <Link
            to={item.to}
            target="_blank"
            rel="noopener noreferrer"
            className={commonClass}
            onClick={() => setMenuOpen(false)}
          >
            <img
              className={cls.menuCardIcon}
              src={item.icon}
              alt=""
              loading="lazy"
            />
            <span className={cls.menuCardLabel}>{item.label}</span>
            <span className={cls.menuCardChevron} aria-hidden="true">
              ↗
            </span>
          </Link>
        </li>
      );
    }

    // external link
    if (item.type === "external") {
      return (
        <li>
          <a
            href={item.href}
            target="_blank"
            rel="noreferrer"
            className={commonClass}
            onClick={() => setMenuOpen(false)}
          >
            <img
              className={cls.menuCardIcon}
              src={item.icon}
              alt=""
              loading="lazy"
            />
            <span className={cls.menuCardLabel}>{item.label}</span>
            <span className={cls.menuCardChevron} aria-hidden="true">
              ↗
            </span>
          </a>
        </li>
      );
    }

    // action button
    if (item.type === "action") {
      return (
        <li>
          <button
            type="button"
            className={commonClass}
            onClick={() => {
              setMenuOpen(false);
              item.onClick?.();
            }}
          >
            <img
              className={cls.menuCardIcon}
              src={item.icon}
              alt=""
              loading="lazy"
            />
            <span className={cls.menuCardLabel}>{item.label}</span>
            <span className={cls.menuCardChevron} aria-hidden="true">
              ›
            </span>
          </button>
        </li>
      );
    }

    return null;
  };

  return (
    <>
      <main className={cls.home}>
        <section className={`nu-shell ${cls.shell}`}>
          <div className={cls.stage}>
            {/* Top-left actions */}
            <div className={cls.topLeftBar}>
              {/* Profile Button */}
              <Motion.button
                type="button"
                className={`${cls.iconBtn} ${cls.profileBtn} ${
                  cls[`profileBtn--${citizenshipTheme}`]
                }`}
                variants={fadeIn}
                initial="hidden"
                animate="show"
                aria-label={tLoungeLayout("profileAriaLabel")}
                onClick={() => navigate("/lounge/my-profile")}
              >
                <span
                  className={`${cls.avatarShell} ${
                    isCouple ? cls["avatarShell--couple"] : ""
                  } ${cls[`avatarShell--${citizenshipTheme}`]}`}
                >
                  <span className="avatar-wrap">
                    <img src={avatarSrc} alt="" className="avatar avatar-sm" />
                  </span>

                  {mlInitialLoading && (
                    <span className={cls.avatarSpinner} aria-hidden="true" />
                  )}

                  {isCouple ? (
                    <span
                      className={`${cls.genderBadge} ${cls.genderBadgePair}`}
                      aria-hidden="true"
                      title={tLoungeLayout("couple")}
                    >
                      <span className={cls.genderBadgeItem}>
                        <img
                          className={cls.genderBadgeImg}
                          src={genderSrc}
                          alt=""
                          draggable="false"
                        />
                      </span>
                      {partnerGenderSrc && (
                        <span className={cls.genderBadgeItem}>
                          <img
                            className={cls.genderBadgeImg}
                            src={partnerGenderSrc}
                            alt=""
                            draggable="false"
                          />
                        </span>
                      )}
                    </span>
                  ) : (
                    <span
                      className={cls.genderBadge}
                      aria-hidden="true"
                      title={tLoungeLayout("gender")}
                    >
                      <img
                        className={cls.genderBadgeImg}
                        src={genderSrc}
                        alt=""
                        draggable="false"
                      />
                    </span>
                  )}
                </span>

                <span className={cls.profileHi}>
                  {mlInitialLoading ? <>{hiText}, …</> : <>{rawUsername}</>}
                </span>

                {mlError && !mlInitialLoading && (
                  <span className={cls.profileError}>
                    {`⚠️ ${tLoungeLayout("profileRefreshError")}`}
                  </span>
                )}
              </Motion.button>

              {/* Center group (mobile: centered between avatar and menu) */}
              <div className={cls.topCenterActions}>
                {/* Notifications */}
                <Motion.button
                  type="button"
                  className={`${cls.iconBtn} ${cls.notifBtn}`}
                  variants={fadeIn}
                  initial="hidden"
                  animate="show"
                  aria-label={tLoungeLayout("notifications")}
                  onClick={() => navigate("/lounge/my-notifications")}
                >
                  <span className={cls.notificationWrap}>
                    <QuickIcon
                      src={notificationsIcon}
                      alt={tLoungeLayout("notifications")}
                      ring={unreadNotificationsCount > 0 ? "red" : "black"}
                    />
                    {unreadNotificationsCount > 0 && (
                      <span className={cls.notificationBadge}>
                        {unreadNotificationsCount > 99
                          ? "99+"
                          : unreadNotificationsCount}
                      </span>
                    )}
                  </span>
                </Motion.button>

                {/* Wallet */}
                <Motion.button
                  type="button"
                  className={`${cls.iconBtn} ${cls.notifBtn}`}
                  variants={fadeIn}
                  initial="hidden"
                  animate="show"
                  aria-label={tLoungeLayout("wallet")}
                  onClick={() => navigate("/lounge/wallet")}
                >
                  <span className={cls.notificationWrap}>
                    <QuickIcon
                      src={walletIcon}
                      alt={tLoungeLayout("wallet")}
                      ring="black"
                    />
                  </span>
                </Motion.button>

                {/* Chat */}
                <Motion.button
                  type="button"
                  className={`${cls.iconBtn} ${cls.notifBtn}`}
                  variants={fadeIn}
                  initial="hidden"
                  animate="show"
                  aria-label={tLoungeLayout("chat")}
                  onClick={() => navigate("/lounge/chat")}
                >
                  <span className={cls.notificationWrap}>
                    <QuickIcon
                      src={chatIcon}
                      alt={tLoungeLayout("chat")}
                      ring={unreadChatBadgeCount > 0 ? "red" : "black"}
                    />
                    {unreadChatBadgeCount > 0 && (
                      <span className={cls.notificationBadge}>
                        {unreadChatBadgeCount > 99
                          ? "99+"
                          : unreadChatBadgeCount}
                      </span>
                    )}
                  </span>
                </Motion.button>

                {/* Calls */}
                <Motion.button
                  type="button"
                  className={`${cls.iconBtn} ${cls.notifBtn}`}
                  variants={fadeIn}
                  initial="hidden"
                  animate="show"
                  aria-label={tLoungeLayout("calls")}
                  onClick={() => navigate("/lounge/calls")}
                >
                  <span className={cls.notificationWrap}>
                    <QuickIcon
                      src={callIcon}
                      alt={tLoungeLayout("calls")}
                      ring="black"
                    />
                  </span>
                </Motion.button>
              </div>
            </div>

            {/* Menu toggle */}
            <Motion.button
              type="button"
              className={`${cls.iconBtn} ${cls.menuToggle}`}
              variants={fadeIn}
              initial="hidden"
              animate="show"
              aria-expanded={menuOpen ? "true" : "false"}
              aria-controls="home-fullmenu"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label={tLoungeLayout("openMenu")}
            />

            {/* Crisp overlay icon (portaled) */}
            {createPortal(
              <span className={cls.menuIconOverlay} aria-hidden="true">
                <span className={`${cls.iconImgWrap} ${cls.ringPurple}`}>
                  <img
                    src={menuIcon}
                    alt={tLoungeLayout("menu")}
                    className={cls.iconImg}
                  />
                </span>
              </span>,
              document.body,
            )}

            {/* Main content */}
            <div className={cls.main} ref={mainRef}>
              <Outlet
                context={{
                  memberType,
                  isCouple,
                  rawGender,
                  rawPartnerGender,
                  avatarSrc,
                  rawUsername,
                }}
              />
            </div>

            {/* Quick actions */}
            <nav
              className={cls.quickBar}
              aria-label={tLoungeLayout("quickActions")}
            >
              <ul className={cls.quickList}>
                <li>
                  <Link
                    className={cls.roundBtn}
                    to="/lounge"
                    aria-label={tLoungeLayout("home")}
                  >
                    <QuickIcon
                      src={homeIcon}
                      alt={tLoungeLayout("home")}
                      ring="pink"
                      active={pathname === "/lounge"}
                      className={cls.quickIconUniform}
                    />
                  </Link>
                </li>

                <li>
                  <Link
                    className={cls.roundBtn}
                    to="/lounge/friends-feed"
                    aria-label={tLoungeLayout("friendsFeed")}
                  >
                    <QuickIcon
                      src={friendsIcon}
                      alt={tLoungeLayout("friendsFeed")}
                      ring="blue"
                      active={pathname.startsWith("/lounge/friends-feed")}
                      className={cls.quickIconUniform}
                    />
                  </Link>
                </li>

                <li>
                  <Link
                    className={cls.roundBtn}
                    to="/lounge/clubs-feed"
                    aria-label={tLoungeLayout("clubsFeed")}
                  >
                    <QuickIcon
                      src={clubIcon}
                      alt={tLoungeLayout("clubsFeed")}
                      ring="purple"
                      active={pathname.startsWith("/lounge/clubs-feed")}
                      className={cls.quickIconUniform}
                    />
                  </Link>
                </li>

                <li>
                  <Link
                    className={cls.roundBtn}
                    to="/lounge/search"
                    aria-label={tLoungeLayout("search")}
                  >
                    <QuickIcon
                      src={searchIcon}
                      alt={tLoungeLayout("search")}
                      ring="gold"
                      active={pathname.startsWith("/lounge/search")}
                      className={cls.quickIconUniform}
                    />
                  </Link>
                </li>
              </ul>
            </nav>
          </div>
        </section>

        {/* ========================= FULLSCREEN MENU (ONE MENU) ========================= */}
        <div
          id="home-fullmenu"
          className={`${cls.fullmenu} ${menuOpen ? cls["is-open"] : ""}`}
          role="dialog"
          aria-modal="true"
          aria-label={tLoungeLayout("mainMenu")}
        >
          <div
            className={cls.fullmenuBackdrop}
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
          />

          <div className={cls.fullmenuPanel} role="document">
            <button
              ref={closeBtnRef}
              type="button"
              className={`${cls.iconBtn} ${cls.menuClose}`}
              onClick={() => setMenuOpen(false)}
              aria-label={tLoungeLayout("closeMenu")}
            >
              <img
                src={menuIcon}
                alt={tLoungeLayout("menu")}
                className={cls.iconImg}
              />
            </button>

            <div className={cls.fullmenuInner}>
              <div className={cls.menuSectionGrid}>
                {menuSections.map((section) => (
                  <section className={cls.menuSection} key={section.title}>
                    <h3 className={cls.menuSectionTitle}>{section.title}</h3>

                    <ul className={cls.menuCardList}>
                      {section.items.map((item, idx) => (
                        <MenuItem key={`${section.title}-${idx}`} item={item} />
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>

      <CallOverlay />
      <ScrollRestoration getKey={(loc) => loc.pathname} />
      {/* <FooterMobile /> */}
      {/* <FooterDesktop /> */}
    </>
  );
}
